import React, { useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  LineChart, Line, AreaChart, Area, PieChart, Pie, Legend,
} from 'recharts'
import { BarChart2, Sparkles, Loader2, Send, Download, FileDown, Table } from 'lucide-react'
import { useMetricsPdf } from '../../pdf/useMetricsPdf'
import { useWireAuth } from '../../auth/useWireAuth'
import type { MetricPoint, MetricsResponse } from './types'
import { CHART_COLOR, CHART_COLORS } from './constants'

// ── API ───────────────────────────────────────────────────────────────────────

const CAFE_API_URL =
  (import.meta.env.VITE_CAFE_API_URL as string | undefined) ??
  'https://cafe-prod.whiteboardfederalrewired.com'

async function queryMetrics(
  prompt: string,
  userId: string,
  cid: string,
): Promise<MetricsResponse> {
  const res = await fetch(CAFE_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt, action: 'metrics', user: userId, cid }),
  })
  if (!res.ok) throw new Error(`Request failed: ${res.status}`)
  const data = await res.json()

  const metricLabel = String(data.structured_output?.key ?? 'Metrics')
  const rawValues: unknown[] = Array.isArray(data.structured_output?.value)
    ? data.structured_output.value
    : []

  const points: MetricPoint[] = rawValues.map((v: unknown) => {
    const row = v as Record<string, unknown>
    const entries = Object.entries(row)
    const find = (name: string) => entries.find(([k]) => k.toLowerCase() === name)?.[0]
    const countKey = find('count')
    const keyKey   = find('key')
    const valueKey = find('value')
    const xKey     = find('x')
    const yKey     = find('y')
    if (countKey !== undefined) {
      const labelEntry = entries.find(([k]) => k !== countKey)
      return { label: String(labelEntry?.[1] ?? ''), value: Number(row[countKey]) }
    }
    if (keyKey !== undefined && valueKey !== undefined) {
      return { label: String(row[keyKey]), value: Number(row[valueKey]) }
    }
    if (xKey !== undefined && yKey !== undefined) {
      return { label: String(row[xKey]), value: Number(row[yKey]) }
    }
    if (entries.length === 2) {
      const numEntry = entries.find(([, val]) => typeof val === 'number')
      const strEntry = entries.find(([, val]) => typeof val === 'string')
      if (numEntry && strEntry) {
        return { label: String(strEntry[1]), value: Number(numEntry[1]) }
      }
    }
    return { label: '', value: 0 }
  })

  const validPoints = points.filter(
    p => p.label.trim() !== '' && Number.isFinite(p.value) && p.value > 0,
  )

  const raw = String(data.output?.[0]?.text ?? data.answer ?? '')
  const thinkingMatch = raw.match(/<thinking>([\s\S]*?)<\/thinking>/i)
  const thinking = thinkingMatch ? thinkingMatch[1].trim() : ''
  const answer = raw
    .replace(/<thinking>[\s\S]*?<\/thinking>/gi, '')
    .replace(/<\/?response>/gi, '')
    .trim()
  return { answer, thinking, metricLabel, points: validPoints, rawRows: rawValues }
}

// ── CSV download ──────────────────────────────────────────────────────────────

function downloadCsv(rows: unknown[], filename: string) {
  if (rows.length === 0) return
  const first = rows[0] as Record<string, unknown>
  const cols = Object.keys(first).filter(
    k => typeof first[k] === 'string' || typeof first[k] === 'number',
  )
  const escape = (v: unknown) => {
    const s = String(v ?? '')
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"`
      : s
  }
  const lines = [
    cols.join(','),
    ...rows.map(r => cols.map(c => escape((r as Record<string, unknown>)[c])).join(',')),
  ]
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

// ── Chart SVG helpers ─────────────────────────────────────────────────────────

function cloneChartSvg(wrapperEl: HTMLDivElement): SVGSVGElement | null {
  const svgEl = wrapperEl.querySelector<SVGSVGElement>('.recharts-wrapper > svg')
  if (!svgEl) return null
  const clone = svgEl.cloneNode(true) as SVGSVGElement
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
  clone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink')
  clone.setAttribute('overflow', 'visible')
  clone.querySelectorAll('[fill]').forEach(el => {
    const f = el.getAttribute('fill')
    if (f?.startsWith('var(')) el.setAttribute('fill', CHART_COLOR)
  })
  clone.querySelectorAll('[stroke]').forEach(el => {
    const s = el.getAttribute('stroke')
    if (s?.startsWith('var(')) el.setAttribute('stroke', '#e0e0e0')
  })
  clone.querySelectorAll('text').forEach(el => {
    if (!el.getAttribute('fill') || el.getAttribute('fill')?.startsWith('var(')) {
      el.setAttribute('fill', '#444')
    }
  })
  return clone
}

function downloadChartSvg(wrapperEl: HTMLDivElement, filename: string) {
  const clone = cloneChartSvg(wrapperEl)
  if (!clone) return
  const serializer = new XMLSerializer()
  const svgStr = '<?xml version="1.0" encoding="UTF-8"?>\n' + serializer.serializeToString(clone)
  const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

async function captureChartPng(wrapperEl: HTMLDivElement): Promise<string | null> {
  const clone = cloneChartSvg(wrapperEl)
  if (!clone) return null
  const svgEl = wrapperEl.querySelector<SVGSVGElement>('.recharts-wrapper > svg')
  if (!svgEl) return null
  const { width, height } = svgEl.getBoundingClientRect()
  const serializer = new XMLSerializer()
  const svgStr = serializer.serializeToString(clone)
  const svgBlob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' })
  const url = URL.createObjectURL(svgBlob)
  return new Promise(resolve => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = Math.ceil(width) * 2
      canvas.height = Math.ceil(height) * 2
      const ctx = canvas.getContext('2d')
      if (!ctx) { URL.revokeObjectURL(url); resolve(null); return }
      ctx.scale(2, 2)
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, width, height)
      ctx.drawImage(img, 0, 0, width, height)
      URL.revokeObjectURL(url)
      resolve(canvas.toDataURL('image/png'))
    }
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null) }
    img.src = url
  })
}

// ── Thinking block ────────────────────────────────────────────────────────────

function ThinkingBlock({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false)
  if (!text) return null
  const firstLine = text.split('\n').find(l => l.trim()) ?? text
  return (
    <div className="rounded border border-line bg-fill px-4 py-3">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="flex w-full items-center justify-between gap-3 text-left"
        aria-expanded={expanded}
      >
        <span className="font-mono text-[9px] font-bold uppercase tracking-widest text-fg-secondary/50">
          Reasoning
        </span>
        <span className="font-mono text-[9px] text-fg-secondary/40">{expanded ? '▲ collapse' : '▼ expand'}</span>
      </button>
      <p className="mt-2 font-mono text-[11px] leading-relaxed text-fg-secondary/60 whitespace-pre-wrap">
        {expanded ? text : <span className="line-clamp-1">{firstLine}</span>}
      </p>
    </div>
  )
}

// ── Typewriter hook ───────────────────────────────────────────────────────────

function useTypewriter(text: string, speed = 4) {
  const [displayed, setDisplayed] = useState('')
  useEffect(() => {
    setDisplayed('')
    if (!text) return
    let i = 0
    const timer = setInterval(() => {
      i++
      setDisplayed(text.slice(0, i))
      if (i >= text.length) clearInterval(timer)
    }, speed)
    return () => clearInterval(timer)
  }, [text, speed])
  return displayed
}

// ── Chart type ────────────────────────────────────────────────────────────────

type ChartType = 'bar' | 'line' | 'area' | 'pie'

const CHART_TYPES: { type: ChartType; label: string }[] = [
  { type: 'bar',  label: 'Bar'  },
  { type: 'line', label: 'Line' },
  { type: 'area', label: 'Area' },
  { type: 'pie',  label: 'Pie'  },
]

// ── MetricsChart ──────────────────────────────────────────────────────────────

function MetricsChart({
  data,
  label,
  captureRef,
}: {
  data: MetricPoint[]
  label: string
  captureRef?: React.MutableRefObject<(() => Promise<string | null>) | null>
}) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [chartType, setChartType] = useState<ChartType>('bar')

  useEffect(() => {
    if (captureRef) {
      captureRef.current = () =>
        wrapperRef.current ? captureChartPng(wrapperRef.current) : Promise.resolve(null)
    }
  })

  const CHAR_WIDTH = 6
  const ANGLE_RAD = 35 * (Math.PI / 180)
  const maxLabelLen = Math.max(...data.map(d => d.label.length), 0)
  const bottomMargin = Math.max(40, Math.ceil(maxLabelLen * CHAR_WIDTH * Math.sin(ANGLE_RAD)) + 12)
  const chartHeight = chartType === 'pie' ? 420 : 260 + bottomMargin

  const tooltipStyle = {
    fontSize: 12,
    borderRadius: 6,
    border: '1px solid var(--color-line, #e0e0e0)',
    background: 'var(--color-surface, #fff)',
    color: 'var(--color-fg, #111)',
  }
  const axisTick = { fontSize: 11, fill: 'var(--color-fg-secondary, #666)' }
  const xAxisProps = {
    dataKey: 'label' as const,
    tick: axisTick,
    tickLine: false,
    axisLine: { stroke: 'var(--color-line, #e0e0e0)' },
    angle: -35 as const,
    textAnchor: 'end' as const,
    interval: 0,
  }
  const yAxisProps = { tick: axisTick, tickLine: false, axisLine: false }

  function handleDownload() {
    if (wrapperRef.current) {
      const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
      downloadChartSvg(wrapperRef.current, `metrics-${slug || 'chart'}.svg`)
    }
  }

  return (
    <div className="rounded border border-line bg-surface p-5">
      <div className="mb-4 flex items-center justify-between gap-3">
        <p className="text-[10px] font-bold uppercase tracking-widest text-fg-secondary">{label}</p>
        <div className="flex items-center gap-2">
          <div className="flex rounded border border-line overflow-hidden">
            {CHART_TYPES.map(({ type, label: tLabel }) => (
              <button
                key={type}
                type="button"
                onClick={() => setChartType(type)}
                className={`px-2.5 py-1 font-sans text-[10px] font-semibold uppercase tracking-widest transition-colors ${
                  chartType === type
                    ? 'bg-fg text-canvas'
                    : 'text-fg-secondary hover:bg-fill hover:text-fg'
                }`}
              >
                {tLabel}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={handleDownload}
            title="Download chart as SVG"
            className="flex items-center gap-1.5 rounded border border-line px-2 py-1 font-sans text-[10px] font-semibold uppercase tracking-widest text-fg-secondary transition-colors hover:border-line-strong hover:bg-fill hover:text-fg"
          >
            <Download size={10} aria-hidden="true" />
            Download Chart as SVG
          </button>
        </div>
      </div>
      <div ref={wrapperRef} style={chartType === 'pie' ? { overflow: 'visible' } : undefined}>
        <ResponsiveContainer width="100%" height={chartHeight}>
          {chartType === 'bar' ? (
            <BarChart data={data} margin={{ top: 4, right: 16, bottom: bottomMargin, left: 8 }}>
              <XAxis {...xAxisProps} />
              <YAxis {...yAxisProps} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'var(--color-fill, rgba(0,0,0,0.04))' }} />
              <Bar dataKey="value" radius={[3, 3, 0, 0]}>
                {data.map((_, i) => <Cell key={i} fill={CHART_COLOR} />)}
              </Bar>
            </BarChart>
          ) : chartType === 'line' ? (
            <LineChart data={data} margin={{ top: 4, right: 16, bottom: bottomMargin, left: 8 }}>
              <XAxis {...xAxisProps} />
              <YAxis {...yAxisProps} />
              <Tooltip contentStyle={tooltipStyle} />
              <Line type="monotone" dataKey="value" stroke={CHART_COLOR} strokeWidth={2} dot={{ fill: CHART_COLOR, r: 4 }} activeDot={{ r: 6 }} />
            </LineChart>
          ) : chartType === 'area' ? (
            <AreaChart data={data} margin={{ top: 4, right: 16, bottom: bottomMargin, left: 8 }}>
              <defs>
                <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={CHART_COLOR} stopOpacity={0.18} />
                  <stop offset="95%" stopColor={CHART_COLOR} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis {...xAxisProps} />
              <YAxis {...yAxisProps} />
              <Tooltip contentStyle={tooltipStyle} />
              <Area type="monotone" dataKey="value" stroke={CHART_COLOR} strokeWidth={2} fill="url(#areaGrad)" dot={{ fill: CHART_COLOR, r: 3 }} />
            </AreaChart>
          ) : (
            <PieChart margin={{ top: 40, right: 80, bottom: 20, left: 80 }}>
              <Pie
                data={data}
                dataKey="value"
                nameKey="label"
                cx="50%"
                cy="50%"
                outerRadius={95}
                label={({ name, percent }) => `${name} (${((percent ?? 0) * 100).toFixed(0)}%)`}
                labelLine={true}
              >
                {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
            </PieChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  )
}

// ── MetricsTable ──────────────────────────────────────────────────────────────

function MetricsTable({ rows }: { rows: unknown[] }) {
  if (rows.length === 0) return null
  const firstRow = rows[0] as Record<string, unknown>
  const columns = Object.keys(firstRow).filter(
    k => typeof firstRow[k] === 'string' || typeof firstRow[k] === 'number',
  )
  if (columns.length === 0) return null
  return (
    <div className="overflow-hidden rounded border border-line">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-line bg-fill">
            {columns.map(col => (
              <th key={col} className="px-4 py-2 font-sans text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const r = row as Record<string, unknown>
            return (
              <tr key={i} className={`border-b border-line-subtle last:border-0 ${i % 2 === 1 ? 'bg-surface' : 'bg-canvas'}`}>
                {columns.map(col => (
                  <td key={col} className="px-4 py-2.5 font-sans text-[12px] text-fg">
                    {String(r[col] ?? '—')}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ── AiAnalysisAnswer ──────────────────────────────────────────────────────────

function AiAnalysisAnswer({ result, prompt }: { result: MetricsResponse; prompt: string }) {
  const displayed = useTypewriter(result.answer)
  const isDone = displayed.length >= result.answer.length
  const { downloadPdf, isGenerating } = useMetricsPdf()
  const captureRef = useRef<(() => Promise<string | null>) | null>(null)

  async function handleDownloadPdf() {
    const chartImageUrl = captureRef.current ? (await captureRef.current()) ?? undefined : undefined
    downloadPdf(result, prompt, chartImageUrl)
  }

  const slug = prompt.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 48).replace(/-+$/, '')

  return (
    <div className="space-y-5">
      <div className="flex justify-end gap-2">
        {(result.rawRows ?? []).length > 0 && (
          <button
            type="button"
            onClick={() => downloadCsv(result.rawRows ?? [], `metrics-${slug || 'data'}.csv`)}
            className="flex items-center gap-1.5 rounded border border-line px-2 py-1 font-sans text-[10px] font-semibold uppercase tracking-widest text-fg-secondary transition-colors hover:border-line-strong hover:bg-fill hover:text-fg"
          >
            <Table size={10} aria-hidden="true" />
            Export Table as CSV
          </button>
        )}
        <button
          type="button"
          onClick={handleDownloadPdf}
          disabled={isGenerating}
          className="flex items-center gap-1.5 rounded border border-line px-2 py-1 font-sans text-[10px] font-semibold uppercase tracking-widest text-fg-secondary transition-colors hover:border-line-strong hover:bg-fill hover:text-fg disabled:pointer-events-none disabled:opacity-40"
        >
          {isGenerating
            ? <Loader2 size={10} className="animate-spin" aria-hidden="true" />
            : <FileDown size={10} aria-hidden="true" />}
          {isGenerating ? 'Building…' : 'Download as PDF'}
        </button>
      </div>
      {result.thinking && <ThinkingBlock text={result.thinking} />}
      {result.points.length > 0 ? (
        <MetricsChart data={result.points} label={result.metricLabel} captureRef={captureRef} />
      ) : (result.rawRows ?? []).length === 0 ? (
        <div className="flex items-center gap-2 rounded border border-warning-line bg-warning-fill px-4 py-3 font-sans text-sm text-warning-fg">
          <BarChart2 size={14} className="shrink-0" aria-hidden="true" />
          No graphable data was returned for this query.
        </div>
      ) : null}
      {(result.rawRows ?? []).length > 0 && (
        <>
          <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Data</p>
          <MetricsTable rows={result.rawRows ?? []} />
        </>
      )}
      <div>
        <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">Analysis</p>
        <p className="font-serif text-base leading-relaxed text-fg whitespace-pre-wrap">
          {displayed}
          {!isDone && (
            <span className="ml-px inline-block w-[2px] h-[1em] align-middle bg-fg animate-pulse" />
          )}
        </p>
      </div>
    </div>
  )
}

// ── AiAnalysisPanel (public) ──────────────────────────────────────────────────

export function AiAnalysisPanel() {
  const { user } = useWireAuth()
  const userId = (user as { sub?: string } | null)?.sub ?? 'anonymous'
  const cidRef = useRef(crypto.randomUUID())

  const [prompt, setPrompt]       = useState('')
  const [lastPrompt, setLastPrompt] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult]       = useState<MetricsResponse | null>(null)
  const [error, setError]         = useState<string | null>(null)

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const trimmed = prompt.trim()
    if (!trimmed || isLoading) return

    setIsLoading(true)
    setError(null)
    setResult(null)
    setLastPrompt(trimmed)

    try {
      const data = await queryMetrics(trimmed, userId, cidRef.current)
      setResult(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="flex gap-2">
        <textarea
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              e.currentTarget.form?.requestSubmit()
            }
          }}
          placeholder="Ask a metrics question… e.g. How many articles were published per region last month?"
          rows={2}
          disabled={isLoading}
          className="flex-1 resize-none rounded border border-line bg-surface px-3 py-2 font-sans text-sm text-fg placeholder:text-fg-secondary focus:border-line-strong focus:outline-none disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={!prompt.trim() || isLoading}
          aria-label="Submit metrics question"
          className="flex h-auto items-center gap-1.5 self-stretch rounded border border-line bg-surface px-4 font-sans text-[11px] font-semibold text-fg-secondary transition-colors hover:border-line-strong hover:bg-fill hover:text-fg disabled:pointer-events-none disabled:opacity-40"
        >
          {isLoading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          {isLoading ? 'Analyzing…' : 'Ask'}
        </button>
      </form>

      {isLoading && (
        <div className="flex items-center gap-2 text-fg-secondary">
          <Loader2 size={14} className="animate-spin" />
          <span className="font-sans text-[12px]">Querying intelligence corpus…</span>
        </div>
      )}

      {error && (
        <p className="rounded border border-error-line bg-error-fill px-4 py-3 font-sans text-sm text-error-fg">{error}</p>
      )}

      {result && <AiAnalysisAnswer result={result} prompt={lastPrompt} />}

      {!result && !isLoading && !error && (
        <div className="flex flex-col items-center justify-center rounded border border-dashed border-line bg-surface py-20 text-center">
          <Sparkles size={28} className="mb-3 text-fg-secondary" />
          <p className="font-sans text-sm font-semibold text-fg">Ask a metrics question</p>
          <p className="mt-1 max-w-sm font-sans text-[12px] text-fg-secondary">
            Query the intelligence corpus with natural language. Results will be visualized as a chart alongside a written analysis.
          </p>
        </div>
      )}
    </div>
  )
}
